import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";

import { formatAccountTypeLabel } from "@/auth/roles";
import { getUserDetail } from "@/api/users";
import type { DashboardFlightRow } from "@/types/api";
import { FlightTable } from "@/components/FlightTable";
import { PageHeader } from "@/components/PageHeader";
import { Pagination } from "@/components/Pagination";
import { formatDate, formatDuration, formatMetric } from "@/lib/format";

const MPS_TO_KMH = 3.6;
const PAGE_SIZE = 15;
const DAY_MS = 24 * 3600 * 1000;

function formatSpeed(value: number | null): string {
  if (value === null) return "-";
  return `${(value * MPS_TO_KMH).toFixed(1)} km / h`;
}

function parseFlightDate(row: DashboardFlightRow): Date | null {
  const raw = (row as unknown as { date_value?: string }).date_value;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isFinite(d.getTime()) ? d : null;
}

/** Build 30-day activity buckets from a flight list (most recent → oldest). */
function buildActivity(flights: DashboardFlightRow[], days = 30) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const labels: string[] = [];
  const counts: number[] = [];
  const trend: number[] = [];
  const buckets = new Array<number>(days).fill(0);

  for (const f of flights) {
    const d = parseFlightDate(f);
    if (!d) continue;
    d.setHours(0, 0, 0, 0);
    const diff = Math.round((now.getTime() - d.getTime()) / DAY_MS);
    if (diff >= 0 && diff < days) buckets[days - 1 - diff] += 1;
  }

  for (let i = 0; i < days; i++) {
    const day = new Date(now.getTime() - (days - 1 - i) * DAY_MS);
    labels.push(
      day.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    );
    counts.push(buckets[i]);
    const start = Math.max(0, i - 6);
    const window = buckets.slice(start, i + 1);
    const avg = window.length
      ? window.reduce((a, b) => a + b, 0) / window.length
      : 0;
    trend.push(Number(avg.toFixed(2)));
  }
  return { labels, counts, trend };
}

function activeDays(flights: DashboardFlightRow[], withinDays = 30): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const cutoff = now.getTime() - (withinDays - 1) * DAY_MS;
  const seen = new Set<string>();
  for (const f of flights) {
    const d = parseFlightDate(f);
    if (!d) continue;
    d.setHours(0, 0, 0, 0);
    if (d.getTime() < cutoff) continue;
    seen.add(d.toISOString().slice(0, 10));
  }
  return seen.size;
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["users", userId],
    queryFn: () => getUserDetail(userId),
    enabled: Number.isFinite(userId),
  });

  const [page, setPage] = useState(1);

  const flights = data?.flights ?? [];
  const pageCount = Math.max(1, Math.ceil(flights.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * PAGE_SIZE;
  const pageFlights = flights.slice(start, start + PAGE_SIZE);

  const activity = useMemo(() => buildActivity(flights, 30), [flights]);
  const active = useMemo(() => activeDays(flights, 30), [flights]);

  if (isLoading) return <p className="empty-state">Loading…</p>;
  if (isError) {
    return (
      <div className="callout danger">
        <strong>Could not load pilot</strong>
        <p>{error instanceof Error ? error.message : "Unknown error"}</p>
      </div>
    );
  }
  if (!data) return null;

  const roleLabel = formatAccountTypeLabel(data.account_type);
  const { overview } = data;

  return (
    <>
      <PageHeader
        title={data.username}
        lede={`${roleLabel} · ${flights.length} recorded flights · last seen ${formatDate(overview.last_flight_at)}`}
        actions={
          <Link className="button ghost" to={`/flights?user_id=${data.id}`}>
            Open in Flights
          </Link>
        }
      />

      <section className="user-profile__hero">
        <div className="user-profile__stats">
          <article className="user-profile__stat">
            <span>Flights</span>
            <strong>{overview.flight_count ?? flights.length}</strong>
            <small>{overview.mission_count ?? 0} mission{overview.mission_count === 1 ? "" : "s"}</small>
          </article>
          <article className="user-profile__stat">
            <span>Total time</span>
            <strong>{formatDuration(overview.total_duration_seconds)}</strong>
            <small>avg {formatDuration(overview.avg_duration_seconds)}</small>
          </article>
          <article className="user-profile__stat">
            <span>Avg speed</span>
            <strong>{formatSpeed(overview.avg_speed_mps)}</strong>
            <small>avg distance {formatMetric(overview.avg_distance_m, 0, " m")}</small>
          </article>
          <article className="user-profile__stat">
            <span>Smoothness</span>
            <strong>
              {overview.avg_smoothness_score !== null
                ? `${Math.round(overview.avg_smoothness_score)}/100`
                : "—"}
            </strong>
            <small>peak altitude {formatMetric(overview.max_altitude_m, 0, " m")}</small>
          </article>
          <article className="user-profile__stat">
            <span>Active days</span>
            <strong>{active}</strong>
            <small>in the last 30 days</small>
          </article>
        </div>

        <article className="user-profile__activity">
          <header>
            <span className="eyebrow">Activity · last 30 days</span>
            <span className="user-profile__activity-meta">
              {activity.counts.reduce((a, b) => a + b, 0)} flights
            </span>
          </header>
          <div className="user-profile__activity-chart">
            <ReactECharts
              option={buildActivityOption(activity)}
              notMerge
              style={{ height: "100%", width: "100%" }}
            />
          </div>
        </article>
      </section>

      <section className="user-profile__meta">
        <article className="panel">
          <div className="panel-head">
            <h2>Account</h2>
          </div>
          <dl className="detail-grid">
            <div>
              <dt>Username</dt>
              <dd>{data.username}</dd>
            </div>
            <div>
              <dt>Role</dt>
              <dd>{roleLabel}</dd>
            </div>
            <div>
              <dt>Joined</dt>
              <dd>{formatDate(data.created_at)}</dd>
            </div>
            <div>
              <dt>Last flight</dt>
              <dd>{formatDate(overview.last_flight_at)}</dd>
            </div>
          </dl>
        </article>
      </section>

      <section className="panel user-profile__history">
        <div className="panel-head">
          <h2>Flight history</h2>
          <p>
            {flights.length
              ? `Showing ${start + 1}–${start + pageFlights.length} of ${flights.length}`
              : "No flights yet"}
          </p>
        </div>
        {flights.length ? (
          <>
            <FlightTable
              rows={pageFlights}
              columns={["session", "date", "duration", "distance", "avg_speed", "smoothness", "status"]}
            />
            <Pagination
              page={currentPage}
              pageCount={pageCount}
              total={flights.length}
              itemLabel="flights"
              onChange={setPage}
            />
          </>
        ) : (
          <p className="empty-state">
            This {roleLabel.toLowerCase()} does not have any assigned flights yet.
          </p>
        )}
      </section>
    </>
  );
}

interface ActivityBuckets {
  labels: string[];
  counts: number[];
  trend: number[];
}

function buildActivityOption(activity: ActivityBuckets) {
  return {
    animation: false,
    grid: { top: 10, right: 8, bottom: 24, left: 24, containLabel: true },
    tooltip: {
      trigger: "axis" as const,
      backgroundColor: "rgba(10,14,20,0.96)",
      borderColor: "rgba(42,52,68,0.5)",
      textStyle: { color: "#E8ECF1", fontSize: 12 },
    },
    xAxis: {
      type: "category" as const,
      data: activity.labels,
      axisLine: { lineStyle: { color: "rgba(42,52,68,0.5)" } },
      axisTick: { show: false },
      axisLabel: {
        color: "#A0AAB8",
        fontSize: 10,
        interval: 4,
      },
    },
    yAxis: {
      type: "value" as const,
      minInterval: 1,
      axisLabel: { color: "#A0AAB8", fontSize: 10 },
      axisLine: { show: false },
      splitLine: {
        lineStyle: { color: "rgba(42,52,68,0.35)", type: "dashed" as const },
      },
    },
    series: [
      {
        name: "Flights",
        type: "bar" as const,
        data: activity.counts,
        barMaxWidth: 14,
        itemStyle: { color: "#FF6B1A", borderRadius: [3, 3, 0, 0] as [number, number, number, number] },
      },
      {
        name: "Trend",
        type: "line" as const,
        data: activity.trend,
        smooth: 0.3,
        showSymbol: false,
        lineStyle: { width: 2, color: "#FBBF24" },
        areaStyle: { color: "rgba(251, 191, 36, 0.1)" },
      },
    ],
  };
}
